/**
 * <shop-the-look> — Shop The Look section behaviour.
 *
 * - Per-item add-to-bag: size pill opens a variant popover; picking a size POSTs to
 *   /cart/add.js and dispatches `cart:update` so the cart drawer refreshes/opens (no reload).
 * - Bodyscan: opens the "What's this?" info popup and the QR hand-off drawer (focus-trapped,
 *   Escape to close), and flips the on/off toggle switch.
 * - Carousels: lazily loads self-hosted Swiper for the mobile docked product carousel.
 * - QR code: generated client-side via the theme's qr-code-generator.js (dynamic import).
 *
 * Recommended-size logic (which size the pill/label shows for a logged-in scanned user) is a
 * documented follow-up — see docs/shop-the-look-implementation.md.
 */

class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.qrLib = this.dataset.qrLib;
    this.swiperJs = this.dataset.swiperJs;
    this.swiperCss = this.dataset.swiperCss;
    /** @type {HTMLElement|null} */
    this.lastFocused = null;
    /** @type {HTMLElement|null} */
    this.activeModal = null;

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocClick, true);

    this.#initCarousels();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocClick, true);
  }

  // ---------------------------------------------------------------- events
  #onClick = (event) => {
    const el = /** @type {HTMLElement} */ (event.target);

    const sizeToggle = el.closest('[data-stl-size-toggle]');
    if (sizeToggle) {
      event.preventDefault();
      this.#toggleSizePop(sizeToggle.closest('[data-stl-size-picker]'));
      return;
    }

    const addBtn = el.closest('[data-stl-add]');
    if (addBtn && addBtn.getAttribute('aria-disabled') !== 'true') {
      event.preventDefault();
      this.#addToBag(addBtn);
      return;
    }

    if (el.closest('[data-stl-open-info]')) {
      event.preventDefault();
      this.#openModal(this.#info(), el.closest('[data-stl-open-info]'));
      return;
    }

    if (el.closest('[data-stl-open-qr]')) {
      event.preventDefault();
      this.#openModal(this.#qr(), el.closest('[data-stl-open-qr]'));
      this.#renderQr();
      return;
    }

    const toggle = el.closest('[data-stl-toggle]');
    if (toggle) {
      event.preventDefault();
      this.#toggleSwitch(toggle);
      return;
    }

    if (el.closest('[data-stl-close]') || el.matches('[data-stl-overlay]')) {
      this.#closeModals();
      return;
    }

    const thumb = el.closest('[data-stl-ugc-thumb]');
    if (thumb) {
      const index = Number(thumb.dataset.stlUgcThumb) || 0;
      if (this.ugcSwiper) this.ugcSwiper.slideTo(index);
      else this.#setActiveSet(index);
      return;
    }

    if (el.closest('[data-stl-see-items]')) {
      event.preventDefault();
      this.#openItems(el.closest('[data-stl-see-items]'));
      return;
    }

    if (el.closest('[data-stl-items-close]')) {
      this.#closeItems();
      return;
    }

    if (!el.closest('[data-stl-size-picker]')) this.#closeSizePops();
  };

  #onDocClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#closeSizePops();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.#itemsOpen()) this.#closeItems();
      else if (this.#modalOpen()) this.#closeModals();
      else this.#closeSizePops();
      return;
    }
    if (event.key === 'Tab' && this.activeModal && !this.activeModal.hidden) {
      const focusables = this.activeModal.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = /** @type {HTMLElement} */ (focusables[0]);
      const last = /** @type {HTMLElement} */ (focusables[focusables.length - 1]);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  // ---------------------------------------------------------------- size popover
  #toggleSizePop(picker) {
    if (!picker) return;
    const pop = picker.querySelector('[data-stl-size-pop]');
    const btn = picker.querySelector('[data-stl-size-toggle]');
    if (!pop) return;
    const isOpen = !pop.hidden;
    this.#closeSizePops();
    if (!isOpen) {
      pop.hidden = false;
      btn?.setAttribute('aria-expanded', 'true');
    }
  }

  #closeSizePops() {
    this.querySelectorAll('[data-stl-size-pop]').forEach((p) => {
      p.hidden = true;
    });
    this.querySelectorAll('[data-stl-size-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  // ---------------------------------------------------------------- add to bag
  async #addToBag(btn) {
    const id = btn.dataset.variantId;
    if (!id) return;
    btn.setAttribute('aria-busy', 'true');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = root.replace(/\/$/, '');
    try {
      const res = await fetch(`${base}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (res.ok) {
        const cart = await fetch(`${base}/cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: 'shop-the-look', data: {} },
          })
        );
      }
    } catch (e) {
      /* swallow — no-reload best effort */
    }
    this.#closeSizePops();
    btn.removeAttribute('aria-busy');
  }

  // ---------------------------------------------------------------- bodyscan modals
  #info() {
    return this.querySelector('[data-stl-info]');
  }
  #qr() {
    return this.querySelector('[data-stl-qr-drawer]');
  }
  #overlay() {
    return this.querySelector('[data-stl-overlay]');
  }
  #modalOpen() {
    return (this.#info() && !this.#info().hidden) || (this.#qr() && !this.#qr().hidden);
  }

  #openModal(modal, trigger) {
    if (!modal) return;
    if (!this.#modalOpen()) this.lastFocused = trigger || /** @type {HTMLElement} */ (document.activeElement);
    // only one modal at a time
    [this.#info(), this.#qr()].forEach((m) => {
      if (m && m !== modal) m.hidden = true;
    });
    const overlay = this.#overlay();
    if (overlay) overlay.hidden = false;
    modal.hidden = false;
    this.activeModal = modal;
    const focusable = modal.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (focusable) /** @type {HTMLElement} */ (focusable).focus();
  }

  #closeModals() {
    [this.#info(), this.#qr(), this.#overlay()].forEach((m) => {
      if (m) m.hidden = true;
    });
    this.activeModal = null;
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    this.lastFocused = null;
  }

  async #renderQr() {
    const holder = this.querySelector('[data-stl-qr]');
    if (!holder || holder.dataset.rendered || !this.qrLib) return;
    const url = holder.dataset.qrUrl;
    if (!url) return;
    try {
      const mod = await import(this.qrLib);
      // eslint-disable-next-line no-new
      new mod.QRCode(holder, {
        text: url,
        width: 160,
        height: 160,
        colorDark: '#1c1917',
        colorLight: '#faf6e8',
      });
      holder.dataset.rendered = '1';
    } catch (e) {
      /* QR is a progressive enhancement */
    }
  }

  #toggleSwitch(btn) {
    const on = btn.getAttribute('aria-checked') === 'true';
    btn.setAttribute('aria-checked', String(!on));
    const wrap = btn.closest('[data-stl-bodyscan]');
    if (wrap) wrap.dataset.active = String(!on);
  }

  // ---------------------------------------------------------------- carousels
  async #initCarousels() {
    const dock = this.querySelector('[data-stl-dock]');
    const ugc = this.querySelector('[data-stl-ugc-swiper]');
    if (!dock && !ugc) return;
    await this.#ensureSwiper();
    if (!window.Swiper) return;

    if (dock) {
      // eslint-disable-next-line no-new
      new window.Swiper(dock, { slidesPerView: 1.3, spaceBetween: 8 });
    }
    if (ugc) this.#initUgcMedia(ugc);
  }

  // ---------------------------------------------------------------- UGC media carousel
  #initUgcMedia(el) {
    // Prep videos so the active slide can autoplay inline (muted) and loop.
    el.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
    });

    const dots = this.querySelector('[data-stl-ugc-dots]');
    this.ugcSwiper = new window.Swiper(el, {
      slidesPerView: 1,
      speed: 500,
      pagination: dots ? { el: dots, type: 'bullets', clickable: true } : false,
      on: {
        init: (sw) => this.#onUgcSlide(sw),
        slideChange: (sw) => this.#onUgcSlide(sw),
      },
    });
  }

  #onUgcSlide(sw) {
    this.#setActiveSet(sw.activeIndex);
    // Play the active slide's video (if any), pause the rest — like the PDP gallery.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sw.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === sw.activeIndex && !reduce) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  #setActiveThumb(index) {
    this.querySelectorAll('[data-stl-ugc-thumb]').forEach((t) => {
      t.classList.toggle('is-active', (Number(t.dataset.stlUgcThumb) || 0) === index);
    });
  }

  /**
   * Swap every per-look ("set") group to the given index: the active thumbnail, the
   * attribution text, the desktop product list and the mobile items carousel — so a thumb
   * click or a centre-carousel swipe changes text + products + media together. Only used by
   * the metaobject-driven (`data-stl-ugc-sets`) layout; the legacy block layout has none of
   * these `[data-stl-set-*]` groups, so the toggles are simply no-ops there.
   */
  #setActiveSet(index) {
    this.activeSet = index;
    this.#setActiveThumb(index);
    this.#toggleSet('data-stl-set-text', index);
    this.#toggleSet('data-stl-set-products', index);
    this.#toggleSet('data-stl-set-items', index);
    this.#syncItemsCount();
    // If the items drawer is already open, re-point its swiper to the now-active look.
    if (this.#itemsOpen()) this.#ensureItemsSwiper();
  }

  #toggleSet(attr, index) {
    this.querySelectorAll(`[${attr}]`).forEach((el) => {
      el.hidden = Number(el.getAttribute(attr)) !== index;
    });
  }

  /** Reflect the active look's product count in the "See Items (N)" button and drawer title. */
  #syncItemsCount() {
    // Only the metaobject (ugc_sets) layout has per-look item carousels; the legacy layout
    // renders a single static count in Liquid, so leave it untouched.
    const active = this.querySelector('[data-stl-set-items]:not([hidden])');
    if (!active) return;
    const count = active.querySelectorAll('.swiper-slide').length;
    this.querySelectorAll('[data-stl-see-count], [data-stl-items-count]').forEach((n) => {
      n.textContent = String(count);
    });
    const seeBtn = this.querySelector('[data-stl-see-items]');
    if (seeBtn) seeBtn.hidden = count === 0;
  }

  // ---------------------------------------------------------------- items drawer (mobile)
  #items() {
    return this.querySelector('[data-stl-items]');
  }

  #itemsOpen() {
    const drawer = this.#items();
    return !!(drawer && !drawer.hidden);
  }

  async #openItems(trigger) {
    const drawer = this.#items();
    if (!drawer) return;
    clearTimeout(this.itemsHideTimer);
    this.itemsOpener = trigger || /** @type {HTMLElement} */ (document.activeElement);
    drawer.hidden = false;
    // Force a reflow so the sheet transitions up from its hidden (translateY 100%) state.
    // (rAF is throttled in background/automation tabs, so it can't be relied on here.)
    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    document.documentElement.setAttribute('scroll-lock', '');

    this.#ensureItemsSwiper();

    const focusable = drawer.querySelector('button, a[href], [tabindex]:not([tabindex="-1"])');
    if (focusable) /** @type {HTMLElement} */ (focusable).focus();
  }

  #closeItems() {
    const drawer = this.#items();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.removeAttribute('scroll-lock');
    clearTimeout(this.itemsHideTimer);
    this.itemsHideTimer = setTimeout(() => {
      drawer.hidden = true;
    }, 320);
    if (this.itemsOpener && typeof this.itemsOpener.focus === 'function') this.itemsOpener.focus();
    this.itemsOpener = null;
  }

  /**
   * Initialise (or refresh) the items-drawer carousel. In the metaobject layout there is one
   * carousel per look and only the active one is visible, so we init the active
   * `[data-stl-set-items]` container (caching the instance on the element). The legacy layout
   * has a single `[data-stl-items-swiper]` carousel, kept on `this.itemsSwiper`.
   */
  #ensureItemsSwiper() {
    if (!window.Swiper) return;
    const active = this.querySelector('[data-stl-set-items]:not([hidden])');
    if (active instanceof HTMLElement) {
      if (active._stlSwiper) active._stlSwiper.update();
      else active._stlSwiper = new window.Swiper(active, { slidesPerView: 1.3, spaceBetween: 8 });
      return;
    }
    const carousel = this.querySelector('[data-stl-items-swiper]');
    if (!(carousel instanceof HTMLElement)) return;
    if (this.itemsSwiper) this.itemsSwiper.update();
    else this.itemsSwiper = new window.Swiper(carousel, { slidesPerView: 1.3, spaceBetween: 8 });
  }

  #ensureSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (window.__stlSwiperLoading) return window.__stlSwiperLoading;
    window.__stlSwiperLoading = new Promise((resolve) => {
      if (this.swiperCss && !document.querySelector(`link[href="${this.swiperCss}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = this.swiperCss;
        document.head.appendChild(link);
      }
      if (this.swiperJs && !document.querySelector(`script[src="${this.swiperJs}"]`)) {
        const script = document.createElement('script');
        script.src = this.swiperJs;
        document.head.appendChild(script);
      }
      // Poll with setTimeout (NOT rAF — rAF is throttled in background tabs, see cart-upsell.js).
      const check = () => (window.Swiper ? resolve() : setTimeout(check, 100));
      check();
    });
    return window.__stlSwiperLoading;
  }
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
